import { Component, Inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { AuthClientService } from '@rsm/client/auth-client';
import { SecurityService, config, IConfig, User } from '@rsm/shared/security';
import { DarkModeService } from './core/theme';
import { OneSignalWebService } from './core/onesignal-web.service';
import { CometChatAuthService } from './chat/core/cometchat-auth.service';
import { CometChatListenerService } from './chat/core/cometchat-listener.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  /** Trava para não disparar duas renovações de token ao mesmo tempo. */
  private refreshing = false;

  constructor(
    private authClientService: AuthClientService,
    private securityService: SecurityService,
    @Inject(config) private config: IConfig,
    private router: Router,
    private darkModeService: DarkModeService,
    private oneSignal: OneSignalWebService,
    private cometChatAuth: CometChatAuthService,
    private cometChatListener: CometChatListenerService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.darkModeService.init();
    this.initSecurity();
    await this.oneSignal.init();
    // Reafirma o External ID (userId) no OneSignal quando ha sessao restaurada.
    // Antes so o login manual chamava registerUser: quem ja estava logado (sessao
    // persistida) nunca tinha o External ID vinculado, e o push nao chegava.
    if (this.securityService.isValid()) {
      const userId = this.securityService.credential?.user?.id;
      if (userId) {
        await this.oneSignal.registerUser(String(userId));
      }
    }
    await this.restoreCometChatSession();
  }

  private async restoreCometChatSession(): Promise<void> {
    const token = localStorage.getItem('rsm_comet_token');
    const uid   = localStorage.getItem('rsm_comet_uid');
    if (!token || !this.securityService.isValid()) return;
    try {
      await this.cometChatAuth.loginWithToken(token);
      this.cometChatListener.register(uid ?? '');
    } catch {
      localStorage.removeItem('rsm_comet_token');
      localStorage.removeItem('rsm_comet_uid');
    }
  }

  private initSecurity(): void {
    this.securityService.onForbidden.subscribe(() => {
      this.router.navigate([this.config.rootRoute]);
    });

    this.securityService.onUnauthorized.subscribe(() => {
      this.router.navigate([this.config.loginRoute]);
      this.securityService.invalidate();
    });

    if (window.location.pathname !== '/validar-email') {
      this.securityService.onRefresh.subscribe((refreshToken: string) => {
        this.refreshSession(refreshToken);
      });
    }

    this.securityService.init();
  }

  /**
   * Renovação de sessão em segundo plano. Roda silenciosa (`silent: true`, sem
   * overlay de carregamento) e nunca em paralelo consigo mesma — chamadas
   * concorrentes gastariam o mesmo refresh token e a segunda voltaria 401,
   * derrubando a sessão de quem só trocou de aba.
   */
  private refreshSession(refreshToken: string): void {
    if (this.refreshing || !refreshToken) {
      return;
    }

    this.refreshing = true;

    this.authClientService.refresh({ refreshToken }, { silent: true }).subscribe({
      next: (data: any) => {
        this.refreshing = false;

        const user: User = {
          id: data.userId,
          name: data.name,
          login: data.login,
          email: data.email,
          roles: data.roles,
          profile: data.profile,
          context: data.context,
          contextId: data.contextId,
          isRoot: data.isRoot,
          accessToken: data.accessToken,
          expiresIn: data.accessExpiresIn,
          refreshToken: data.refreshToken,
          userContextId: data.userContextId,
          expiredPassword: data.temporaryPassword,
        };

        this.securityService.init(user);
      },
      error: () => {
        this.refreshing = false;
        // Falha aqui não é ação do usuário: o SecurityService já tem um retry
        // agendado e o 401 cai no fluxo de sessão expirada. Um toast de erro
        // só assustaria alguém que não pediu nada.
      },
    });
  }
}
